import { useState, useEffect } from 'react'
import Hero from '../components/Hero'
import Carousel from '../components/Carousel'
import EventsCarousel from '../components/EventsCarousel'
import Gallery, { type GalleryAlbum } from '../components/Gallery'
import Partners from '../components/Partners'
import Newsletter from '../components/Newsletter'
import FeaturedArtists from '../components/FeaturedArtists'
import EventHighlights from '../components/EventHighlights'
import { API_BASE_URL } from '../config/api'
import { isShaanBanner, openShaanBridgePage } from '../utils/shaanBanner'

interface HeroItem {
  id: number
  type: 'image' | 'video'
  title: string
  redirect_url?: string
  file_path?: string
  file_url?: string
  is_active?: number
}

interface Event {
  id: number
  title: string
  subtitle?: string
  date: string
  googledrivelink: string
  location: string
  image_path?: string
  image?: string
  thumbnail_path?: string
  thumbnail?: string
  description: string
  status: 'upcoming' | 'past'
  ticket_url?: string
  end_date?: string
}

const Home = () => {
  const [showCarousel, setShowCarousel] = useState(false)
  const [activeAboutTab, setActiveAboutTab] = useState<'story' | 'journey' | 'support'>('story')
  const [activeVisionMissionTab, setActiveVisionMissionTab] = useState<'vision' | 'mission'>('vision')
  
  // Dynamic data states
  const [heroItems, setHeroItems] = useState<HeroItem[]>([])
  const [dbHasHeroItems, setDbHasHeroItems] = useState(false)
  const [events, setEvents] = useState<Event[]>([])
  const [aboutSections, setAboutSections] = useState<any[]>([])
  const [partners, setPartners] = useState<any[]>([])
  const [eventGalleries, setEventGalleries] = useState<GalleryAlbum[]>([])
  // const [loadingHero, setLoadingHero] = useState(true)
  // const [loadingEvents, setLoadingEvents] = useState(true)
  // const [loadingAbout, setLoadingAbout] = useState(true)
  

  // Extract start date from display date format (for UI display)
  // Handles both formats: "22 March 2026 12:24pm to 22 March 2026 3:30pm" or "22 March 2026 12:24pm"
  const extractStartDateForDisplay = (displayDate: string): string => {
    if (!displayDate) return ''
    
    // If it contains " to ", extract the start part (before " to ")
    if (displayDate.includes(' to ')) {
      return displayDate.split(' to ')[0].trim()
    }
    
    // Otherwise return as is
    return displayDate
  }

  // Parse date string to Date object for sorting
  // Handles formats like: "Dec 2023 1:23", "22 March 2026 12:24pm", "22 March 2026 12:24pm to 22 March 2026 3:30pm"
  const parseDateForSorting = (dateString: string): Date => {
    if (!dateString) return new Date(0) // Return epoch date for invalid dates
    
    // Extract start date if it contains " to "
    let dateToParse = dateString
    if (dateString.includes(' to ')) {
      dateToParse = dateString.split(' to ')[0].trim()
    }
    
    try {
      // Try parsing formats like "Dec 2023 1:23" or "December 2023 1:23"
      // Pattern: Month Year Hour:Minute
      const monthYearTimeMatch = dateToParse.match(/^(\w+)\s+(\d{4})\s+(\d{1,2}):(\d{2})/i)
      if (monthYearTimeMatch) {
        const monthName = monthYearTimeMatch[1]
        const year = parseInt(monthYearTimeMatch[2])
        const hour = parseInt(monthYearTimeMatch[3])
        const minute = parseInt(monthYearTimeMatch[4])
        
        const monthNames = ['january', 'february', 'march', 'april', 'may', 'june', 
                          'july', 'august', 'september', 'october', 'november', 'december']
        const monthNamesShort = ['jan', 'feb', 'mar', 'apr', 'may', 'jun',
                                'jul', 'aug', 'sep', 'oct', 'nov', 'dec']
        
        const monthIndex = monthNames.findIndex(m => m.toLowerCase() === monthName.toLowerCase())
        const monthIndexShort = monthNamesShort.findIndex(m => m.toLowerCase() === monthName.toLowerCase())
        const month = monthIndex !== -1 ? monthIndex : (monthIndexShort !== -1 ? monthIndexShort : 0)
        
        return new Date(year, month, 1, hour, minute)
      }
      
      // Try parsing formats like "22 March 2026 12:24pm"
      // Pattern: Day Month Year Hour:Minute(am/pm)
      const dayMonthYearTimeMatch = dateToParse.match(/(\d+)\s+(\w+)\s+(\d{4})\s+(\d{1,2}):(\d{2})(am|pm)/i)
      if (dayMonthYearTimeMatch) {
        const day = parseInt(dayMonthYearTimeMatch[1])
        const monthName = dayMonthYearTimeMatch[2]
        const year = parseInt(dayMonthYearTimeMatch[3])
        let hours = parseInt(dayMonthYearTimeMatch[4])
        const minutes = parseInt(dayMonthYearTimeMatch[5])
        const ampm = dayMonthYearTimeMatch[6].toLowerCase()
        
        const monthNames = ['january', 'february', 'march', 'april', 'may', 'june', 
                          'july', 'august', 'september', 'october', 'november', 'december']
        const month = monthNames.findIndex(m => m.toLowerCase() === monthName.toLowerCase())
        
        if (ampm === 'pm' && hours !== 12) hours += 12
        if (ampm === 'am' && hours === 12) hours = 0
        
        if (month !== -1) {
          return new Date(year, month, day, hours, minutes)
        }
      }
      
      // Try parsing formats like "March 21, 2026"
      const monthDayYearMatch = dateToParse.match(/(\w+)\s+(\d+),\s+(\d{4})/i)
      if (monthDayYearMatch) {
        const monthName = monthDayYearMatch[1]
        const day = parseInt(monthDayYearMatch[2])
        const year = parseInt(monthDayYearMatch[3])
        
        const monthNames = ['january', 'february', 'march', 'april', 'may', 'june', 
                          'july', 'august', 'september', 'october', 'november', 'december']
        const month = monthNames.findIndex(m => m.toLowerCase() === monthName.toLowerCase())
        
        if (month !== -1) {
          return new Date(year, month, day)
        }
      }
      
      // Try standard Date parsing as fallback
      const parsedDate = new Date(dateToParse)
      if (!isNaN(parsedDate.getTime())) {
        return parsedDate
      }
    } catch (e) {
      console.warn('Error parsing date:', dateToParse, e)
    }
    
    // Return epoch date if parsing fails
    return new Date(0)
  }

  // Default/fallback data
  const defaultCarouselImages = [
    {
      id: 1,
      src: `${import.meta.env.BASE_URL}aashiqui-banner.webp`,
      alt: 'Aashiqui With Kumar Sanu & Sadhana Sargam banner',
      title: 'Aashiqui With Kumar Sanu & Sadhana Sargam',
      description: 'Unforgettable 90s • March 21, 2026 • Ovens Auditorium, Charlotte, NC',
    }
  ]

  const defaultUpcomingEvents = [
    {
      id: 1,
      title: 'Aashiqui With Kumar Sanu & Sadhana Sargam',
      subtitle: 'UNFORGETTABLE 90s',
      date: 'March 21, 2026',
      googledrivelink: '',
      location: 'Ovens Auditorium, Charlotte, NC',
      image: `${import.meta.env.BASE_URL}aashiqui-banner.webp`,
      description: 'Experience the magic of 90s Bollywood music with legendary singers Kumar Sanu and Sadhana Sargam in this unforgettable concert.',
    },
  ]

  // Helper function to get hero file URL
  const getHeroFileUrl = (item: HeroItem): string | null => {
    if (item.file_path) {
      const cleanPath = item.file_path.replace(/\\/g, '/')
      return `${API_BASE_URL}${cleanPath}`
    }
    return item.file_url || null
  }

  // Helper function to get event image URL
  const getEventImageUrl = (event: Event): string => {
    if (event.thumbnail_path) {
      const cleanPath = event.thumbnail_path.replace(/\\/g, '/')
      return `${API_BASE_URL}${cleanPath}`
    }
    if (event.image_path) {
      const cleanPath = event.image_path.replace(/\\/g, '/')
      return `${API_BASE_URL}${cleanPath}`
    }
    return event.image || event.thumbnail || `${import.meta.env.BASE_URL}aashiqui-banner.webp`
  }

  // Calculate status based on end_date
  const calculateStatus = (endDate: string): 'upcoming' | 'past' => {
    if (!endDate) return 'upcoming'
    const end = new Date(endDate)
    const now = new Date()
    return end >= now ? 'upcoming' : 'past'
  }

  // Fetch hero items
  const fetchHeroItems = async () => {
    // setLoadingHero(true)
    try {
      const response = await fetch(`${API_BASE_URL}hero_list.php`)
      const result = await response.json()
      
      if (result.status && result.data) {
        // Filter only active items
        const activeItems = result.data.filter((item: HeroItem) => Number(item.is_active ?? 1) === 1)
        setHeroItems(activeItems)
        setDbHasHeroItems(result.data.length > 0)
      }
    } catch (err) {
      console.error('Error fetching hero items:', err)
      // Keep default/empty state on error
    } 
  }

  // Fetch events
  const fetchEvents = async () => {
    // setLoadingEvents(true)
    try {
      const response = await fetch(`${API_BASE_URL}event_list.php`)
      const result = await response.json()
      
      if (result.status && result.data) {
        // Recalculate status and filter active events
        const activeEvents = result.data
          .filter((event: any) => Number(event.is_active ?? 1) === 1)
          .map((event: any) => {
            if (event.end_date) {
              return { ...event, status: calculateStatus(event.end_date) }
            }
            return event
          })
        setEvents(activeEvents)
      }
    } catch (err) {
      console.error('Error fetching events:', err)
      // Keep default/empty state on error
    } 
  }

  // Fetch about us sections
  const fetchAboutSections = async () => {
    // setLoadingAbout(true)
    try {
      const response = await fetch(`${API_BASE_URL}about_us_list.php`)
      const result = await response.json()
      
      if (result.status && result.data) {
        // Handle both array and object response formats
        if (Array.isArray(result.data)) {
          setAboutSections(result.data.filter((s: any) => Number(s.is_active ?? 1) === 1))
        } else {
          // Convert object to array
          const sectionsArray = Object.values(result.data) as any[]
          setAboutSections(sectionsArray.filter((s: any) => Number(s.is_active ?? 1) === 1))
        }
      }
    } catch (err) {
      console.error('Error fetching about us sections:', err)
      // Keep default/empty state on error
    } 
  }

  // Fetch partners
  const fetchPartners = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}partner_list.php`)
      const result = await response.json()
      
      if (result.status && result.data) {
        // Filter only active partners and sort by display_order
        const activePartners = result.data
          .filter((partner: any) => Number(partner.is_active ?? 1) === 1)
          .sort((a: any, b: any) => {
            const orderA = a.display_order || 999
            const orderB = b.display_order || 999
            return orderA - orderB
          })
          .map((partner: any) => {
            // Get logo URL from API response
            let logoUrl = ''
            if (partner.logo_url) {
              logoUrl = partner.logo_url
            } else if (partner.logo_path) {
              const cleanPath = partner.logo_path.replace(/\\/g, '/')
              logoUrl = `${API_BASE_URL}${cleanPath}`
            } else if (partner.logo && partner.logo.startsWith('http')) {
              logoUrl = partner.logo
            } else if (partner.logo) {
              const cleanPath = partner.logo.replace(/\\/g, '/')
              logoUrl = `${API_BASE_URL}${cleanPath}`
            }
            
            return {
              id: partner.id,
              name: partner.name,
              logo: logoUrl,
              website: partner.website
            }
          })
        setPartners(activePartners)
      }
    } catch (err) {
      console.error('Error fetching partners:', err)
      // Keep default/empty state on error
    }
  }

  // Helper function to get gallery image URL
  const getGalleryImageUrl = (image: any): string => {
    if (image.media_url) return image.media_url
    if (image.image_url) return image.image_url
    if (image.image_path) {
      const cleanPath = image.image_path.replace(/\\/g, '/')
      return `${API_BASE_URL}${cleanPath}`
    }
    return ''
  }

  // Fetch event galleries
  const fetchEventGalleries = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}event_gallery_list.php`)
      const result = await response.json()
      
      if (result.status && result.data && result.data.length > 0) {
        // Group images by event_id
        const galleryMap = new Map<number, any>()
        
        result.data.forEach((image: any) => {
          if (!galleryMap.has(image.event_id)) {
            // Find event name from current events state
            const event = events.find(e => e.id === image.event_id)
            const eventName = event ? event.title : `Event #${image.event_id}`
            
            // Get thumbnail/cover image (prefer thumbnail, otherwise first image)
            const thumbnailImage = result.data.find((img: any) => 
              img.event_id === image.event_id && img.is_thumbnail === 1
            )
            const firstImage = result.data.find((img: any) => img.event_id === image.event_id)
            const coverImageUrl = thumbnailImage 
              ? getGalleryImageUrl(thumbnailImage)
              : (firstImage ? getGalleryImageUrl(firstImage) : getGalleryImageUrl(image))
            
            galleryMap.set(image.event_id, {
              id: image.event_id,
              title: eventName,
              googledrivelink: image.googledrivelink,
              coverImage: coverImageUrl || `${import.meta.env.BASE_URL}aashiqui-banner.webp`,
              images: []
            })
          }
          
          const gallery = galleryMap.get(image.event_id)!
          const imageUrl = getGalleryImageUrl(image)
          if (imageUrl) {
            gallery.images.push({
              id: image.id,
              src: imageUrl,
              alt: image.title || `Gallery Image ${gallery.images.length + 1}`,
              title: image.title,
              googledrivelink: image.googledrivelink,
            })
          }
        })
        
        // Convert to array and sort by event_id (most recent first)
        const galleriesArray = Array.from(galleryMap.values())
          .filter(gallery => gallery.images.length > 0) // Only include galleries with images
          .sort((a, b) => b.id - a.id) // Sort by event_id descending (newest first)
          .slice(0, 3) // Limit to 3 galleries for home page
        
        setEventGalleries(galleriesArray)
      }
    } catch (err) {
      console.error('Error fetching event galleries:', err)
      // Keep default/empty state on error
    }
  }

  useEffect(() => {
    fetchHeroItems()
    fetchEvents()
    fetchAboutSections()
    fetchPartners()
  }, [])

  // Fetch galleries after events are loaded (to get event names)
  useEffect(() => {
    if (events.length > 0) {
      fetchEventGalleries()
    }
  }, [events])

  // Automatically switch to Carousel if there are active images but no active videos
  useEffect(() => {
    if (heroItems.length > 0) {
      const hasActiveVideo = heroItems.some(item => item.type === 'video')
      setShowCarousel(!hasActiveVideo)
    } else {
      setShowCarousel(false)
    }
  }, [heroItems])

  // Get about section
  const aboutSection = aboutSections.find(s => s.section === 'about')
  const visionSection = aboutSections.find(s => s.section === 'vision')
  const missionSection = aboutSections.find(s => s.section === 'mission')

  // Get carousel images from hero items or use default
  const carouselImages = heroItems.length > 0
    ? heroItems
        .filter(item => item.type === 'image')
        .map(item => ({
          id: item.id,
          src: getHeroFileUrl(item) || defaultCarouselImages[0].src,
          alt: item.title,
          title: item.title,
          description: item.title,
          redirect_url: item.redirect_url,
        }))
    : (dbHasHeroItems ? [] : defaultCarouselImages)

  // Get active hero video if any
  const activeVideoItem = heroItems.find(item => item.type === 'video')
  const heroVideoUrl = activeVideoItem ? getHeroFileUrl(activeVideoItem) : undefined

  // Get active hero banner image if any
  const activeImageItem = heroItems.find(item => item.type === 'image')
  const heroBannerImage = activeImageItem ? getHeroFileUrl(activeImageItem) : undefined
  const heroBannerUrl = activeImageItem?.redirect_url || activeVideoItem?.redirect_url 
  const heroBannerTitle = activeImageItem?.title || activeVideoItem?.title

  const handleHeroBannerClick = () => {
    const title = heroBannerTitle || activeImageItem?.title || activeVideoItem?.title || ''
    const url = heroBannerUrl
    if (isShaanBanner(title)) {
      openShaanBridgePage()
    } else if (url) {
      window.open(url, '_blank', 'noopener,noreferrer')
    }
  }

  const handleCarouselSlideClick = (image: { title?: string; redirect_url?: string; alt?: string }) => {
    const slideTitle = image.title || image.alt || ''
    if (isShaanBanner(slideTitle)) {
      openShaanBridgePage()
    } else {
      const targetUrl = image.redirect_url || heroBannerUrl
      if (targetUrl) {
        window.open(targetUrl, '_blank', 'noopener,noreferrer')
      }
    }
  }
  
  // Separate upcoming and past events, then sort by date in descending order
  const upcomingEvents = events.length > 0
    ? events
        .filter(event => event.status === 'upcoming')
        .sort((a, b) => {
          const dateA = parseDateForSorting(a.date)
          const dateB = parseDateForSorting(b.date)
          return dateB.getTime() - dateA.getTime() // Descending order (newest first)
        })
        .map(event => ({
          id: event.id,
          title: event.title,
          subtitle: event.subtitle,
          date: extractStartDateForDisplay(event.date),
          googledrivelink: event.googledrivelink,
          location: event.location,
          image: getEventImageUrl(event),
          description: event.description,
          ticket_url: event.ticket_url,
        }))
    : defaultUpcomingEvents.sort((a, b) => {
        const dateA = parseDateForSorting(a.date)
        const dateB = parseDateForSorting(b.date)
        return dateB.getTime() - dateA.getTime() // Descending order (newest first)
      })

  const pastEvents = events
    .filter(event => event.status === 'past')
    .sort((a, b) => {
      const dateA = parseDateForSorting(a.date)
      const dateB = parseDateForSorting(b.date)
      return dateB.getTime() - dateA.getTime() // Descending order (newest first)
    })
    .map(event => ({
      id: event.id,
      title: event.title,
      description: event.description,
      image: getEventImageUrl(event),
      googledrivelink: event.googledrivelink,
      display_date: extractStartDateForDisplay(event.date),
      stats: event.location,
    }))

    

  // Default gallery albums (fallback if API fails or no galleries)
  const defaultGalleryAlbums: GalleryAlbum[] = [
    {
      id: 1,
      title: 'Event Flyers & Posters',
      googledrivelink: '',
      coverImage: `${import.meta.env.BASE_URL}aashiqui-banner.webp`,
      images: [
        { id: 1, googledrivelink: '', src: `${import.meta.env.BASE_URL}aashiqui-banner.webp`, alt: 'Aashiqui concert banner', title: 'Aashiqui With Kumar Sanu & Sadhana Sargam' },
        { id: 2, googledrivelink: '', src: `${import.meta.env.BASE_URL}Kumar-Sanu-updated-online-flye.webp`, alt: 'Kumar Sanu flyer', title: 'Kumar Sanu Live' },
        { id: 3, googledrivelink: '', src: `${import.meta.env.BASE_URL}New-flyer-with-sponsor.webp`, alt: 'Event flyer with sponsors', title: 'Event Sponsors' },
        { id: 4, googledrivelink: '', src: `${import.meta.env.BASE_URL}aashiqui-banner.webp`, alt: 'QR code ticket flyer', title: 'Scan to Buy Tickets' },
      ],
    },
    {
      id: 2,
      title: 'Meet & Greet',
      googledrivelink: '',
      coverImage: `${import.meta.env.BASE_URL}Meet-Greet-50.webp`,
      images: [
        { id: 5, googledrivelink: '', src: `${import.meta.env.BASE_URL}Meet-Greet-50.webp`, alt: 'Meet and Greet 50', title: 'Meet & Greet Moments' },
        { id: 6, googledrivelink: '', src: `${import.meta.env.BASE_URL}Meet-Greet-51.webp`, alt: 'Meet and Greet 51', title: 'Fans with Artists' },
        { id: 7, googledrivelink: '', src: `${import.meta.env.BASE_URL}Meet-Greet-52.webp`, alt: 'Meet and Greet 52', title: 'Backstage Candid' },
        { id: 8, googledrivelink: '', src: `${import.meta.env.BASE_URL}Meet-Greet-55.webp`, alt: 'Meet and Greet 55', title: 'Celebration Together' },
      ],
    },
    {
      id: 3,
      title: 'Event Performances',
      googledrivelink: '',
      coverImage: `${import.meta.env.BASE_URL}superhit.webp`,
      images: [
        { id: 9, googledrivelink: '', src: `${import.meta.env.BASE_URL}superhit.webp`, alt: 'Superhit event collage', title: 'Superhit Performances' },
      ],
    },
  ]

  // Use API galleries if available, otherwise use default
  const galleryAlbums = eventGalleries.length > 0 ? eventGalleries : defaultGalleryAlbums

  // Default partners (fallback if API fails or no partners)
  const defaultPartners = [
    { id: 1, name: 'Triveni Supermarket', logo: `${import.meta.env.BASE_URL}sponsor-1.webp` },
    { id: 2, name: 'Nachbaliye Academy', logo: `${import.meta.env.BASE_URL}sponsor-2.webp` },
    { id: 3, name: 'Rapport Global', logo: `${import.meta.env.BASE_URL}sponsor-3.webp` },
    { id: 4, name: 'Fusion Group Games', logo: `${import.meta.env.BASE_URL}sponsor-4.webp` },
  ]

  // Use API partners if available, otherwise use default
  const displayPartners = partners.length > 0 ? partners : defaultPartners

  const featuredArtists = [
    { id: 1, name: 'Sonu Nigam', image: `${import.meta.env.BASE_URL}sonu-nigam.jpg`, description: 'Bollywood Superstar' },
    { id: 2, name: 'Kumar Sanu', image: `${import.meta.env.BASE_URL}kumar-sanu.jpg`, description: 'Legendary Singer' },
    { id: 3, name: 'Sadhana Sargam', image: `${import.meta.env.BASE_URL}sadhana-sargam.jpg`, description: 'Playback Singer' },
    { id: 4, name: 'Neha Kakkar', image: `${import.meta.env.BASE_URL}neha-kakar.jpg`, description: 'Pop Sensation' },
    { id: 5, name: 'Shreya Ghoshal', image: `${import.meta.env.BASE_URL}shreyas-goyal.jpg`, description: 'Melody Queen' },
    { id: 6, name: 'Atif Aslam', image: `${import.meta.env.BASE_URL}atif-aslam.jpg`, description: 'Pakistani Sensation' },
  ]

  // Default event highlights (fallback)
  const defaultEventHighlights = [
    {
      id: 1,
      title: 'Aashiqui With Kumar Sanu & Sadhana Sargam',
      description: 'An unforgettable evening of 90s Bollywood classics that brought back memories and created new ones.',
      googledrivelink: '',
      image: `${import.meta.env.BASE_URL}aashiqui-banner.webp`,
      display_date: '2024',
      stats: '5,000+ Attendees',
    },
    {
      id: 2,
      title: 'Sau Saal Pehle - A Tribute to Mohammed Rafi',
      description: 'Sonu Nigam paid a beautiful tribute to the legendary Mohammed Rafi in this spectacular tour.',
      googledrivelink: '',
      image: `${import.meta.env.BASE_URL}New-flyer-with-sponsor.webp`,
      display_date: '2023',
      stats: '10+ Cities',
    },
    {
      id: 3,
      title: 'Niladri Kumar\'s Dream India',
      description: 'A mesmerizing journey through Indian classical music that captivated audiences across North America.',
      googledrivelink: '',
      image: `${import.meta.env.BASE_URL}superhit.webp`,
      display_date: '2023',
      stats: 'Sold Out',
    },
  ]

  // Use past events from API or default highlights
  const eventHighlights = pastEvents.length > 0 ? pastEvents : defaultEventHighlights

  const handleVideoEnd = () => {
    setShowCarousel(true)
  }

  return (
    <div className="w-full relative">
      {/* Hero Video / Carousel Section */}
      {(!dbHasHeroItems || heroItems.length > 0) && (
        <>
          {/* Hero Video Section */}
          {!showCarousel && (
            <Hero
              onVideoEnd={handleVideoEnd}
              bannerUrl={heroBannerUrl}
              bannerTitle={heroBannerTitle}
              bannerImage={(heroBannerImage || carouselImages[0]?.src) || undefined}
              videoUrl={heroVideoUrl || undefined}
              onBannerClick={handleHeroBannerClick}
            />
          )}
          
          {/* Image Carousel Section */}
          {showCarousel && (
            <Carousel
              images={carouselImages}
              autoPlayInterval={5000}
              bannerUrl={heroBannerUrl}
              onSlideClick={handleCarouselSlideClick}
            />
          )}
        </>
      )}
      
      {/* Event Countdown Section */}
      {/* <EventCountdown
        eventDate="2025-03-21T19:00:00"
        eventTitle="Aashiqui With Kumar Sanu & Sadhana Sargam"
        eventLocation="Ovens Auditorium, Charlotte, NC"
      /> */}
      
      {/* Stats Section */}
      {/* <section className="bg-gray-800 py-12 md:py-16 relative overflow-hidden animated-bg">
        <div className="floating-shapes"></div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-primary-light mb-2">
                <AnimatedCounter end={500} suffix="+" duration={2000} />
              </div>
              <div className="text-gray-400 text-sm md:text-base">Events</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-primary-light mb-2">
                <AnimatedCounter end={1600} suffix="+" duration={2000} />
              </div>
              <div className="text-gray-400 text-sm md:text-base">Community Members</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-primary-light mb-2">
                <AnimatedCounter end={100} suffix="+" duration={2000} />
              </div>
              <div className="text-gray-400 text-sm md:text-base">Programs</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-primary-light mb-2">
                <AnimatedCounter end={25} suffix="+" duration={2000} />
              </div>
              <div className="text-gray-400 text-sm md:text-base">Upcoming Events</div>
            </div>
          </div>
        </div>
      </section> */}

      {/* About Us Section with Tabs */}
      <section className="bg-gray-900 py-12 md:py-20 relative overflow-hidden animated-bg">
        <div className="particle-bg"></div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-6xl mx-auto">
            {/* <div className="text-center mb-12">
              <span className="text-primary-light text-sm md:text-base font-semibold uppercase tracking-wider">
                About Us
              </span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mt-4 mb-6 px-4">
                Compassion-driven healthcare, born from family and service.
              </h2>
            </div> */}

            {/* Tab Navigation - Only 3 tabs for About Us sections */}
            <div className="bg-gray-800 rounded-t-lg border-b border-gray-700">
              <div className="flex flex-wrap justify-center gap-2 md:gap-4 p-4">
                <button
                  onClick={() => setActiveAboutTab('story')}
                  className={`px-4 md:px-6 py-3 text-base md:text-lg font-semibold transition-all duration-300 rounded-lg min-h-[44px] ${
                    activeAboutTab === 'story'
                      ? 'text-primary-light bg-gray-700 border-b-2 border-primary-light'
                      : 'text-gray-400 hover:text-primary-light'
                  }`}
                >
                  Our Story
                </button>
                <button
                  onClick={() => setActiveAboutTab('journey')}
                  className={`px-4 md:px-6 py-3 text-base md:text-lg font-semibold transition-all duration-300 rounded-lg min-h-[44px] ${
                    activeAboutTab === 'journey'
                      ? 'text-primary-light bg-gray-700 border-b-2 border-primary-light'
                      : 'text-gray-400 hover:text-primary-light'
                  }`}
                >
                  The Turning Point
                </button>
                <button
                  onClick={() => setActiveAboutTab('support')}
                  className={`px-4 md:px-6 py-3 text-base md:text-lg font-semibold transition-all duration-300 rounded-lg min-h-[44px] ${
                    activeAboutTab === 'support'
                      ? 'text-primary-light bg-gray-700 border-b-2 border-primary-light'
                      : 'text-gray-400 hover:text-primary-light'
                  }`}
                >
                  How You Can Help
                </button>
              </div>
            </div>

            {/* Tab Content */}
            <div className="bg-gray-800 rounded-b-lg p-8 md:p-12">
              {/* Tab #1 */}
              {activeAboutTab === 'story' && (
                <div className="animate-fadeIn">
                  <div className="bg-gray-900 rounded-lg overflow-hidden">
                    <div className="grid md:gap-8 p-6 md:p-8 lg:p-12">
                      <div className="flex flex-col justify-center">
                        <h3 className="text-3xl md:text-4xl font-bold text-primary-light mb-6">
                          Our Story
                        </h3>
                        <div className="text-gray-300 text-lg md:text-xl leading-relaxed">
                          {aboutSection?.intro_content ? (
                            <div dangerouslySetInnerHTML={{ __html: aboutSection.intro_content }} />
                          ) : (
                            <p>
                              LMT SEVA named in loving memory of my father, Lalji Tiwari—was born out of personal loss and a deep desire to help others.
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Tab #2 */}
              {activeAboutTab === 'journey' && (
                <div className="animate-fadeIn">
                  <div className="bg-gray-900 rounded-lg overflow-hidden">
                    <div className="grid gap-6 md:gap-8 p-6 md:p-8 lg:p-12">
                      <div className="flex flex-col justify-center">
                        <h3 className="text-3xl md:text-4xl font-bold text-primary-light mb-6">
                          The Turning Point
                        </h3>
                        <div className="text-gray-300 text-lg md:text-xl leading-relaxed">
                          {aboutSection?.hospital_journey_content ? (
                            <div dangerouslySetInnerHTML={{ __html: aboutSection.hospital_journey_content }} />
                          ) : (
                            <>
                              <p>
                                We opened LMT SEVA Hospital in Gorakhpur, India as a charity hospital offering
                                essential care at minimal cost. For nearly two years it became a place of hope
                                for patients who had nowhere else to go.
                              </p>
                              <p className="mt-4">
                                Financial strain and the challenge of attracting doctors to a remote location
                                forced us to pause operations. Now, we are working to relocate the hospital to
                                a city so doctors can commute easily and patients can access care without
                                barriers.
                              </p>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Tab #3 */}
              {activeAboutTab === 'support' && (
                <div className="animate-fadeIn">
                  <div className="bg-gray-900 rounded-lg overflow-hidden">
                    <div className="grid gap-6 md:gap-8 p-6 md:p-8 lg:p-12">
                      <div className="flex flex-col justify-center">
                        <h3 className="text-3xl md:text-4xl font-bold text-primary-light mb-6">
                          How You Can Help
                        </h3>
                        <div className="text-gray-300 text-lg md:text-xl leading-relaxed">
                          {aboutSection?.future_vision_content ? (
                            <div dangerouslySetInnerHTML={{ __html: aboutSection.future_vision_content }} />
                          ) : (
                            <ul className="space-y-4">
                              <li className="flex items-start">
                                <span className="text-primary-light mr-3 text-2xl flex-shrink-0">✓</span>
                                <span>Support relocating the hospital to a city location so we can reopen faster.</span>
                              </li>
                              <li className="flex items-start">
                                <span className="text-primary-light mr-3 text-2xl flex-shrink-0">✓</span>
                                <span>Join our concerts and events that raise funds to provide dignified, affordable care.</span>
                              </li>
                              <li className="flex items-start">
                                <span className="text-primary-light mr-3 text-2xl flex-shrink-0">✓</span>
                                <span>Partner or advocate to help us bring skilled doctors and resources to those in need.</span>
                              </li>
                              <li className="flex items-start">
                                <span className="text-primary-light mr-3 text-2xl flex-shrink-0">✓</span>
                                <span>Believe with us that healthcare is a right, not a privilege.</span>
                              </li>
                            </ul>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Events Section */}
      <section className="bg-gray-900 py-12 md:py-20">
        <EventsCarousel events={upcomingEvents} autoPlayInterval={6000} />
        
        {/* Events Grid - Only show if more than 1 event or if you want to show grid separately */}
        {/* {upcomingEvents.length > 1 && (
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 mt-12">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {upcomingEvents.slice(0, 3).map((event:any) => (
                  <div
                    key={event.id}
                    onClick={() => {
                      if (event.googledrivelink) {
                        window.open(event.googledrivelink, '_blank', 'noopener,noreferrer')
                      }
                      
                    }}
                    className="bg-gray-800 rounded-lg overflow-hidden hover:bg-gray-700 transition-colors group"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={event.image}
                        alt={event.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                      <div className="absolute top-4 left-4 bg-primary-light text-white px-3 py-1 rounded-lg text-sm font-semibold">
                        {extractStartDateForDisplay(event.date)}
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-white mb-2 line-clamp-2">
                        {event.title}
                      </h3>
                      {event.subtitle && (
                        <p className="text-primary-light text-sm mb-3">
                          {event.subtitle}
                        </p>
                      )}
                      <div className="flex items-center text-gray-400 text-sm mb-4">
                        <svg
                          className="w-4 h-4 mr-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        </svg>
                        <span className="line-clamp-1">{event.location}</span>
                      </div>
                      <a
                        onClick={(e) => e.stopPropagation()}
                        href={event.ticket_url || "https://www.ticketmaster.com/event/2D006375A9CAB6E2"}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full bg-primary-light hover:bg-primary-dark text-white px-4 py-3 rounded-lg font-semibold transition-colors text-sm md:text-base min-h-[44px] inline-block text-center"
                      >
                        Buy Tickets
                      </a>
                    </div>
                  </div>
                ))}
              </div>
              <div className="text-center mt-8">
                <Link
                  to="/events"
                  className="inline-block bg-gray-800 hover:bg-gray-700 text-white px-6 md:px-8 py-3 rounded-lg font-semibold transition-colors min-h-[44px] text-sm md:text-base"
                >
                  View All Events
                </Link>
              </div>
        </div>
        )} */}
      </section>

      {/* Event Highlights Section */}
      <EventHighlights highlights={eventHighlights} />

      {/* Why Choose Us Section */}
      {/* <section className="bg-gray-800 py-12 md:py-20">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-6xl mx-auto">
                <div className="text-center mb-12">
                  <span className="text-primary-light text-sm md:text-base font-semibold uppercase tracking-wider">
                    Why Choose Us
                  </span>
                  <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mt-4 mb-6">
                    What Makes Us Special
                  </h2>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="bg-gray-900 rounded-lg p-6 text-center hover:bg-gray-800 transition-colors">
                    <div className="text-4xl font-bold text-primary-light mb-4">30+</div>
                    <h3 className="text-xl font-semibold text-white mb-2">Years of Excellence</h3>
                    <p className="text-gray-400">
                      Decades of organizing top-tier Bollywood concerts
                    </p>
                  </div>
                  <div className="bg-gray-900 rounded-lg p-6 text-center hover:bg-gray-800 transition-colors">
                    <div className="text-4xl font-bold text-primary-light mb-4">★</div>
                    <h3 className="text-xl font-semibold text-white mb-2">Exclusive Access</h3>
                    <p className="text-gray-400">
                      To Bollywood's biggest stars & top-tier production
                    </p>
                  </div>
                  <div className="bg-gray-900 rounded-lg p-6 text-center hover:bg-gray-800 transition-colors">
                    <div className="text-4xl font-bold text-primary-light mb-4">✓</div>
                    <h3 className="text-xl font-semibold text-white mb-2">Seamless Execution</h3>
                    <p className="text-gray-400">
                      High-quality production and logistics
                    </p>
                  </div>
                  <div className="bg-gray-900 rounded-lg p-6 text-center hover:bg-gray-800 transition-colors">
                    <div className="text-4xl font-bold text-primary-light mb-4">👥</div>
                    <h3 className="text-xl font-semibold text-white mb-2">Massive Engagement</h3>
                    <p className="text-gray-400">
                      Thousands of attendees at every event
                    </p>
                  </div>
                </div>
              </div>
            </div>
      </section> */}

      {/* Vision & Mission Section with Tabs */}
      <section className="bg-gray-800 py-12 md:py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-primary-light text-sm md:text-base font-semibold uppercase tracking-wider">
                Vision & Mission
              </span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mt-4 mb-6 px-4">
                Our Vision & Mission
              </h2>
            </div>

            {/* Tab Navigation */}
            <div className="bg-gray-900 rounded-t-lg border-b border-gray-700">
              <div className="flex flex-wrap justify-center gap-2 md:gap-4 p-4">
                <button
                  onClick={() => setActiveVisionMissionTab('vision')}
                  className={`px-4 md:px-6 py-3 text-base md:text-lg font-semibold transition-all duration-300 rounded-lg min-h-[44px] ${
                    activeVisionMissionTab === 'vision'
                      ? 'text-primary-light bg-gray-800 border-b-2 border-primary-light'
                      : 'text-gray-400 hover:text-primary-light'
                  }`}
                >
                  Vision
                </button>
                <button
                  onClick={() => setActiveVisionMissionTab('mission')}
                  className={`px-4 md:px-6 py-3 text-base md:text-lg font-semibold transition-all duration-300 rounded-lg min-h-[44px] ${
                    activeVisionMissionTab === 'mission'
                      ? 'text-primary-light bg-gray-800 border-b-2 border-primary-light'
                      : 'text-gray-400 hover:text-primary-light'
                  }`}
                >
                  Mission
                </button>
              </div>
            </div>

            {/* Tab Content */}
            <div className="bg-gray-900 rounded-b-lg p-8 md:p-12">
              {/* Vision Tab */}
              {activeVisionMissionTab === 'vision' && (
                <div className="animate-fadeIn">
                  <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                    {visionSection?.title || 'Our Vision'}
                  </h2>
                  <div className="space-y-6 text-gray-300 text-lg md:text-xl leading-relaxed">
                    {visionSection?.content ? (
                      <div dangerouslySetInnerHTML={{ __html: visionSection.content }} />
                    ) : (
                      <p>
                        To reopen LMT SEVA Hospital in India and build a world where compassion leads healthcare—where every person, regardless of financial means, is treated with dignity, care, and hope.
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Mission Tab */}
              {activeVisionMissionTab === 'mission' && (
                <div className="animate-fadeIn">
                  <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                    {missionSection?.title || 'Our Mission'}
                  </h2>
                  <div className="space-y-6 text-gray-300 text-lg md:text-xl leading-relaxed">
                    {missionSection?.content ? (
                      <div dangerouslySetInnerHTML={{ __html: missionSection.content }} />
                    ) : (
                      <p>
                        Born from personal loss and driven by service, our mission is to stand with those who have nowhere else to turn. Through community support, charitable initiatives, and purpose-driven events, we strive to reopen and sustain LMT SEVA Hospital, providing affordable—and ultimately free—healthcare to underserved communities.
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Artists Section */}
      <FeaturedArtists artists={featuredArtists} />

      {/* Performance Timeline Section */}
      {/* <PerformanceTimeline events={timelineEvents} /> */}

      {/* Testimonials Section */}
      {/* <Testimonials testimonials={testimonials} /> */}

      {/* Gallery Section */}
      <Gallery albums={galleryAlbums} title="Our Event Gallery" />

      {/* Partners Section */}
      <Partners partners={displayPartners} />

      {/* Newsletter Section */}
      <Newsletter />

      {/* CTA Section */}
      {/* <section className="bg-gradient-to-br from-gray-800 via-gray-900 to-gray-800 py-16 md:py-24 relative overflow-hidden animated-bg">
  
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
            backgroundSize: '30px 30px'
          }}></div>
        </div>
        <div className="floating-shapes"></div>
        <div className="particle-bg"></div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-primary-light/20 rounded-full p-4 mb-6">
              <svg className="w-10 h-10 text-primary-light" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Ready to Experience the Magic?
            </h2>
            <p className="text-lg md:text-xl text-gray-300 mb-10 max-w-2xl mx-auto">
              Join us for unforgettable live entertainment experiences featuring Bollywood's biggest stars.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://www.ticketmaster.com/event/2D006375A9CAB6E2"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-primary-light hover:bg-primary-dark text-white px-8 py-4 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl min-h-[56px] text-base md:text-lg inline-block text-center"
              >
                Book Your Tickets
              </a>
              <a
                href="/about-us"
                className="bg-transparent border-2 border-primary-light text-white hover:bg-primary-light hover:text-white px-8 py-4 rounded-xl font-semibold transition-all duration-300 min-h-[56px] flex items-center justify-center text-base md:text-lg"
              >
                Learn More
              </a>
            </div>
          </div>
        </div>
      </section> */}
    </div>
  )
}

export default Home

